import fetcher from './fetcher';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';

import * as Sentry from '@sentry/react';
import { StatusCodes } from 'http-status-codes';
import { useCallback, useRef } from 'react';
import { CRIBL_AGENT_HEADER_NAME, MAESTRO_AGENT } from 'shared/src/observability/constants';

export type AuthenticatedFetcher = <T>(url: string, options?: RequestInit) => Promise<T>;

type UseFetcherHook = {
  fetcher: AuthenticatedFetcher;
};

const useFetcher = (): UseFetcherHook => {
  const auth = useCommonAuthProvider();
  const zeusBaseUrl = process.env.ZEUS_API_URL as string;
  const getAccessTokenRef = useRef(auth.getAccessToken);
  getAccessTokenRef.current = auth.getAccessToken;

  const authenticatedFetcher = useCallback(
    async <T>(url: string, options: RequestInit = {}): Promise<T> => {
      try {
        const token = await getAccessTokenRef.current();
        // Maestro should send the x-cribl-agent header to Zeus to help with attribution on metrics.
        // At the moment, the leader does not support this header over CORS so we should make sure not to send it there
        const targetSpecificHeaders: Record<string, string> = url.startsWith(zeusBaseUrl)
          ? { [CRIBL_AGENT_HEADER_NAME]: MAESTRO_AGENT }
          : {};

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...targetSpecificHeaders,
          ...options.headers,
        };

        return await fetcher<T>(url, { ...options, headers });
      } catch (error: unknown) {
        let status: unknown;
        let errorField: unknown;

        if (typeof error === 'object' && error !== null) {
          const record = error as Record<string, unknown>;
          status = record.status;
          errorField = record.error;
        }

        if (status === StatusCodes.UNAUTHORIZED || errorField === 'login_required' || errorField === 'invalid_grant') {
          Sentry.captureException(error, {
            contexts: {
              api: {
                name: 'AuthenticatedFetcher',
                url,
                status,
                error: errorField,
              },
            },
          });
        }

        throw error;
      }
    },
    [zeusBaseUrl],
  ) as AuthenticatedFetcher;

  return { fetcher: authenticatedFetcher };
};

export default useFetcher;
